// The patient app's build-time environment (Frontend §1.6), validated with zod at startup. Values are trimmed and
// empty counts as unset (Architecture §7). VITE_* values are public: never a secret.
import { z } from 'zod'

/** Trimmed; an empty string counts as unset. */
const blankToUnset = (v: unknown) => (typeof v === 'string' ? v.trim() || undefined : v)
const req = <T extends z.ZodType>(schema: T) => z.preprocess(blankToUnset, schema)
const opt = <T extends z.ZodType>(schema: T) => z.preprocess(blankToUnset, schema.optional())

const origin = z.url({ protocol: /^https?$/ }).transform((u) => u.replace(/\/+$/, ''))
const flag = z.enum(['0', '1']).transform((v) => v === '1')
/** An absolute http(s) URL, or a path on the patient origin (`/…`). */
const scriptUrl = z
  .string()
  .refine((v) => v.startsWith('/') || /^https?:\/\/[^\s]+$/.test(v), 'an http(s) URL or a /path')

export const envSchema = z
  .object({
    VITE_API_ORIGIN: req(origin),
    VITE_API_MOCK: opt(flag).transform((v) => v ?? false),
    VITE_ENV: req(z.enum(['local', 'preview', 'prod'])),
    VITE_DEFAULT_CLINIC_SLUG: opt(z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'a clinic slug')),
    VITE_PATIENT_APP_ORIGIN: opt(origin),
    /**
     * Local dev and E2E only (task 16 assumption; Testing §9.2 "reCAPTCHA stub URL"): load this script instead of
     * Google's `enterprise.js`, e.g. the PF Keeper fake's stub. Refused when `VITE_ENV=prod`.
     */
    VITE_RECAPTCHA_SCRIPT_URL: opt(scriptUrl),
  })
  .superRefine((env, ctx) => {
    if (env.VITE_ENV === 'prod' && env.VITE_RECAPTCHA_SCRIPT_URL !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['VITE_RECAPTCHA_SCRIPT_URL'],
        message: 'VITE_RECAPTCHA_SCRIPT_URL is refused when VITE_ENV=prod',
      })
    }
    if (env.VITE_ENV === 'prod' && env.VITE_API_MOCK) {
      ctx.addIssue({
        code: 'custom',
        path: ['VITE_API_MOCK'],
        message: 'VITE_API_MOCK=1 is refused when VITE_ENV=prod',
      })
    }
  })

export type PatientEnv = z.output<typeof envSchema>

export type EnvResult = { ok: true; env: PatientEnv } | { ok: false; issues: z.core.$ZodIssue[] }

export function parseEnv(raw: Record<string, unknown>): EnvResult {
  const result = envSchema.safeParse(raw)
  return result.success ? { ok: true, env: result.data } : { ok: false, issues: result.error.issues }
}
